import * as React from "react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";

import { UitkPrimaryButton, UitkSecondaryButton } from "uitk-react-button";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { UitkFigureAspectRatioType as ExperimentalAspectRatioType } from "uitk-react-experimental-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutFlexProps } from "uitk-react-layout-flex";
import { UitkLayoutConditionalGridColumnRow } from "uitk-react-layout-grid";

import { FTNodeOptions, ExtendedFTNode, ComponentProps } from "typings/flexFramework/FlexComponent";
import { EditorialFlexModule, FlexModuleResult } from "typings/microserviceModels/content-flex-module";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { withMabTracker, MabTrackEvent } from "src/components/utility/analytics/MabTracker";
import { UitkScrimType as UitkExperimentalScrimType } from "uitk-react-experimental-scrim";
import { FAILED_MODEL_RENDER } from "src/config/systemEvents";
import { NOOP_LOGGER as logger } from "bernie-logger";
import { AnalyticsStore } from "bernie-core";

import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { FlexModuleModelStore } from "stores/FlexModuleModelStore";
import { observer } from "mobx-react";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { withStores } from "stores";
import { SpacingSize } from "src/components/utility/FlexAttributesUtil";

export interface EditorialBannerFTNodeOptions extends FTNodeOptions {}

export interface EditorialBannerFTNode extends ExtendedFTNode {
  options: EditorialBannerFTNodeOptions;
}

export interface EditorialBannerProps extends ComponentProps {
  flexTemplateNode: EditorialBannerFTNode;
  flexModuleModelStore: FlexModuleModelStore;
  mabTrack?: (x: MabTrackEvent) => null;
  analytics: AnalyticsStore;
  isHero?: Boolean;
}

export const EditorialBanner = withMabTracker(
  withStores("flexModuleModelStore")(
    observer((props: EditorialBannerProps) => {
      try {
        const { useRef, useEffect, useState } = React;
        const { mabTrack, flexTemplateNode, flexModuleModelStore, isHero } = props;
        const { id } = flexTemplateNode;
        const model = flexModuleModelStore.getModel(id) as FlexModuleResult;
        const options = normalizeOptionsData(flexTemplateNode, model) as EditorialBannerFTNodeOptions;
        const { fmId } = options;
        const scrimRef = useRef<HTMLElement>(null);
        const [isSmallScrim, setSmallScrim] = useState(false);
        const smallLimitWidth = 800;

        useEffect(() => {
          if (!isSmallScrim && scrimRef && scrimRef.current && scrimRef.current.offsetWidth < smallLimitWidth) {
            setSmallScrim(true);
          } else if (isSmallScrim) {
            setSmallScrim(false);
          }
        }, []);

        /* istanbul ignore next*/
        if (!model) {
          return null;
        }

        const [{ media, text }] = model.items;
        const { padding } = model as EditorialFlexModule;
        /**
         * This is a temporary work around due to limitations of the call-to-action model that will be addressed in the future.
         * Parsing data from contributed HTML content should not be considered an accepted approach to be repeated elsewhere
         */
        const htmlText = text as string;

        const [, title] = /<h3>(.*?)<\/h3>/g.exec(htmlText) || [];
        const [, subtitle] = /<p class="subtitle">(.*?)<\/p>/g.exec(htmlText) || [];
        const [, ctaLabel] = /<p><button>(.*?)<\/button><\/p>/g.exec(htmlText) || [];

        // Media content
        const [desktopMedia, mobileMedia] = media;
        const { mediaUrl: desktopMediaURL } = desktopMedia;
        const { mediaUrl: mobileMediaURL } = mobileMedia;
        const { clickUrl } = desktopMedia;

        // Component display properties
        const hasNoPadding = padding === SpacingSize.NONE;
        const className = "EditorialBanner";
        const spacing: UitkSpacingProps = { margin: { inline: "three", blockstart: hasNoPadding ? "unset" : "three" } };

        const trackingInfo: FlexTrackingInfo = {
          moduleName: isHero ? "EditorialHeroBanner" : "EditorialBanner",
          action: Action.CLICK,
          linkName: "CTA button clicked",
          rfrr: "Banner_Click",
        };

        const cardClicked = mabTrack
          ? () => {
              const mabTrackData: MabTrackEvent = {
                moduleName: "List_your_property",
                eventTarget: "List_your_property",
                eventType: "homepageClick.nonPath",
                rfrr: "LYP_Homepage",
              };
              mabTrack(mabTrackData);
              sendDelayedTrackEvent(trackingInfo, props.analytics);
            }
          : () => {
              sendDelayedTrackEvent(trackingInfo, props.analytics);
            };

        const justifyContent: UitkLayoutFlexProps["justifyContent"] = "center";

        const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 2 };
        const headingCols: UitkLayoutConditionalGridColumnRow = { small: 1, medium: isSmallScrim ? 1 : 2 };

        const CtaButton = isHero ? UitkPrimaryButton : UitkSecondaryButton;
        const largeFontSizeTitle = isHero ? 800 : 700;
        const smallFontSizeTitle = isHero ? 700 : 600;
        const fontSizeSubtitle = isHero || isSmallScrim ? 400 : 500;

        const scrimCardProps = {
          id: `editorial-banner-${fmId}`,
          className,
          titleSlot: (
            <UitkHeading>
              <UitkText size={isSmallScrim ? smallFontSizeTitle : largeFontSizeTitle} theme="inverse">
                {title}
              </UitkText>
            </UitkHeading>
          ),
          subtitleSlot: (
            <UitkText size={fontSizeSubtitle} theme="inverse">
              {subtitle}
            </UitkText>
          ),
          footerSlot: (
            <UitkSpacing margin={{ blockend: "one" }}>
              <div>
                <UitkSpacing margin={{ block: "three" }}>
                  <CtaButton tag="a" onClick={cardClicked} href={clickUrl}>
                    {ctaLabel}
                  </CtaButton>
                </UitkSpacing>
              </div>
            </UitkSpacing>
          ),
          spacing,
          mainGridColumns,
          padding,
          headingCols,
        };

        const largeAspectRatio =
          isHero || isSmallScrim ? ExperimentalAspectRatioType.R21_9 : ExperimentalAspectRatioType.R4_1;
        const smallScrimType = isSmallScrim ? UitkExperimentalScrimType.BOTTOM : UitkExperimentalScrimType.OVERLAY_TOP;

        return (
          <Viewport>
            <ViewSmall>
              <ScrimCard
                {...scrimCardProps}
                aspectRatio={isHero ? ExperimentalAspectRatioType.R3_4 : ExperimentalAspectRatioType.R1_1}
                backgroundImageURL={mobileMediaURL}
                scrimType={smallScrimType}
                marginClass="s-t-padding-zero"
                justifyContent={isSmallScrim ? "end" : justifyContent}
                domRef={scrimRef}
              />
            </ViewSmall>
            <ViewMedium>
              <ScrimCard
                {...scrimCardProps}
                aspectRatio={ExperimentalAspectRatioType.R21_9}
                backgroundImageURL={desktopMediaURL}
                scrimType={UitkExperimentalScrimType.OVERLAY_LEFT}
                marginClass="s-t-padding-zero"
                domRef={scrimRef}
                justifyContent={justifyContent}
              />
            </ViewMedium>
            <ViewLarge>
              <ScrimCard
                {...scrimCardProps}
                aspectRatio={largeAspectRatio}
                backgroundImageURL={desktopMediaURL}
                scrimType={UitkExperimentalScrimType.OVERLAY_LEFT}
                marginClass="s-t-padding-zero"
                domRef={scrimRef}
                justifyContent={justifyContent}
              />
            </ViewLarge>
          </Viewport>
        );
      } catch (error) {
        logger.logEvent(
          FAILED_MODEL_RENDER,
          "Unexpected error occurred while rendering EditorialBanner module.",
          error
        );

        return null;
      }
    })
  )
);

export default EditorialBanner;
